import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ELEMENT_CATEGORIES, ELEMENT_ITEMS } from '../../data/elementsCatalog';
import ElementTile from './ElementTile';

const GRID_COLUMNS = 2;
const PAGE_SIZE = 30;

const ElementsCategoryView = ({ category, activeChip, onBack, onActivateItem, recentItems }) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [focusIndex, setFocusIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubcategory, setActiveSubcategory] = useState('all');
  const tileRefs = useRef([]);

  const isRecent = category?.id === 'recent';
  const isDisabledCategory = category?.disabled;

  const items = useMemo(() => {
    if (!category) return [];
    if (isRecent) return recentItems || [];
    if (activeChip) {
      return ELEMENT_ITEMS.filter((item) => item.kind === activeChip);
    }
    return ELEMENT_ITEMS.filter((item) => item.category === category.id);
  }, [activeChip, category, isRecent, recentItems]);

  const subcategories = useMemo(() => {
    const unique = new Set(items.map((item) => item.subcategory).filter(Boolean));
    return ['all', ...Array.from(unique)];
  }, [items]);

  const filteredItems = useMemo(() => {
    let filtered = items;
    if (activeSubcategory !== 'all') {
      filtered = filtered.filter((item) => item.subcategory === activeSubcategory);
    }
    if (searchTerm.trim().length > 0) {
      const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
      filtered = filtered.filter((item) => {
        const haystack = [
          item.title,
          item.kind,
          item.category,
          item.subcategory,
          ...(item.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      });
    }
    return filtered;
  }, [activeSubcategory, items, searchTerm]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setFocusIndex(0);
    setSearchTerm('');
    setActiveSubcategory('all');
  }, [category?.id, activeChip, isRecent]);

  useEffect(() => {
    const node = tileRefs.current[focusIndex];
    if (node && node.focus) {
      node.focus();
    }
  }, [focusIndex]);

  const handleGridKeyDown = (event) => {
    if (!filteredItems.length) return;
    let nextIndex = focusIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = Math.min(focusIndex + 1, filteredItems.length - 1);
        break;
      case 'ArrowLeft':
        nextIndex = Math.max(focusIndex - 1, 0);
        break;
      case 'ArrowDown':
        nextIndex = Math.min(focusIndex + GRID_COLUMNS, filteredItems.length - 1);
        break;
      case 'ArrowUp':
        nextIndex = Math.max(focusIndex - GRID_COLUMNS, 0);
        break;
      default:
        return;
    }
    event.preventDefault();
    setFocusIndex(nextIndex);
  };

  if (!category) return null;

  const visibleItems = filteredItems.slice(0, visibleCount);
  const canLoadMore = filteredItems.length > visibleCount;
  const showSubFilters = subcategories.length > 1;
  const categoryTitle = category?.title || ELEMENT_CATEGORIES.find((cat) => cat.id === category?.id)?.title;

  return (
    <div className="elements-category">
      <div className="elements-category-header">
        <button type="button" className="elements-back" onClick={onBack}>
          {'<'}
        </button>
        <span>{categoryTitle}</span>
      </div>

      <div className="elements-search">
        <span className="elements-search-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6">
            <circle cx="11" cy="11" r="7" />
            <path d="M16.5 16.5L21 21" />
          </svg>
        </span>
        <input
          type="text"
          placeholder="Search in category"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {showSubFilters && (
        <div className="elements-subfilters">
          {subcategories.map((label) => (
            <button
              key={label}
              type="button"
              className={`elements-chip ${activeSubcategory === label ? 'active' : ''}`}
              onClick={() => setActiveSubcategory(label)}
            >
              {label === 'all' ? 'All' : label}
            </button>
          ))}
        </div>
      )}

      {isDisabledCategory ? (
        <div className="elements-empty">
          This category is coming soon.
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="elements-empty">
          {isRecent ? 'No recently used elements yet.' : 'Catalog loading...'}
        </div>
      ) : (
        <div className="elements-category-grid elements-grid" onKeyDown={handleGridKeyDown}>
          {visibleItems.map((item, index) => (
            <ElementTile
              key={item.catalogId}
              item={item}
              onActivate={onActivateItem}
              tabIndex={index === focusIndex ? 0 : -1}
              buttonRef={(node) => {
                tileRefs.current[index] = node;
              }}
            />
          ))}
        </div>
      )}

      {canLoadMore && (
        <button
          type="button"
          className="elements-load-more"
          onClick={() => setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredItems.length))}
        >
          Load more
        </button>
      )}
      {!isRecent && <div className="elements-helper">Catalog loading...</div>}
    </div>
  );
};

export default ElementsCategoryView;
